import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Carousel, Card } from 'react-bootstrap';
import api from '../utils/api';
import { FaPhone, FaEnvelope, FaLinkedin, FaInstagram, FaBullhorn, FaChartLine, FaTv, FaRobot } from 'react-icons/fa';
import SpotifyStats from '../components/SpotifyStats';
import GhinStats from '../components/GhinStats';
import SEO from '../components/SEO';

const HomePage = () => {
  const [work, setWork] = useState([]);
  const [nonProfits, setNonProfits] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [heroSettings, setHeroSettings] = useState({
    mainHeading: "Hi, I'm Mike Williams",
    subHeading: "I love Erie PA, Marketing, and my Family",
    images: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Contact Form State
  const [contactForm, setContactForm] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    message: '',
    honeypot: '',
    mathAnswer: ''
  });
  const [contactStatus, setContactStatus] = useState(null); // 'sending', 'success', 'error'

  useEffect(() => {
    const fetchData = async () => {
      try {
        const workRes = await api.get('/work');
        const npRes = await api.get('/nonprofits');
        const hlRes = await api.get('/highlights');
        const heroRes = await api.get('/hero');

        setWork(workRes.data);
        setNonProfits(npRes.data);
        setHighlights(hlRes.data);

        // Only update hero if data exists, otherwise defaults are used
        if (heroRes.data) {
            setHeroSettings({
                mainHeading: heroRes.data.mainHeading || "Hi, I'm Mike Williams",
                subHeading: heroRes.data.subHeading || "I love Erie PA, Marketing, and my Family",
                images: heroRes.data.images || []
            });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load content. Please ensure the backend is running.');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
      return (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
              <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
              </div>
          </div>
      );
  }

  if (error) {
      return (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
              <div className="alert alert-danger" role="alert">
                  {error}
              </div>
          </div>
      );
  }

  const API_URL = process.env.REACT_APP_API_URL;

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.id]: e.target.value });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus('sending');
    try {
      await api.post('/contact', contactForm);
      setContactStatus('success');
      setContactForm({ 
        name: '', 
        email: '', 
        phone: '', 
        message: '', 
        honeypot: '', 
        mathAnswer: '' 
      });
    } catch (err) {
      console.error(err);
      // Display specific error message from backend if available (like math error)
      if (err.response && err.response.data && err.response.data.message) {
          alert(err.response.data.message);
          setContactStatus(null); // Reset status to allow retry
      } else {
          setContactStatus('error');
      }
    }
  };

  return (
    <>
      <SEO />
      {/* Hero Section */}
      <header className="masthead" id="home">
        <div className="hero-background">
            <Carousel fade interval={5000} controls={false} indicators={false} pause={false}>
                {heroSettings.images.length > 0 ? (
                    heroSettings.images.map((img, idx) => (
                        <Carousel.Item className="carousel-item-custom" key={idx}>
                            <img src={`${API_URL}${img}`} alt={`Hero Background ${idx + 1}`} />
                        </Carousel.Item>
                    ))
                ) : (
                    <>
                        <Carousel.Item className="carousel-item-custom">
                            <img src={`${API_URL}/uploads/hero-1.jpg`} alt="Background 1" />
                        </Carousel.Item>
                        <Carousel.Item className="carousel-item-custom">
                            <img src={`${API_URL}/uploads/hero-2.jpg`} alt="Background 2" />
                        </Carousel.Item>
                    </>
                )}
            </Carousel>
            <div className="hero-overlay"></div>
        </div>
        <Container className="h-100">
            <div className="row h-100 align-items-center justify-content-center text-center">
                <div className="col-lg-10 align-self-center">
                    <h1 className="text-white fw-bold display-3 mb-4">{heroSettings.mainHeading}</h1>
                    <hr className="divider my-4 bg-light opacity-100 mx-auto" style={{ width: '100px', height: '3px' }} />
                    <h2 className="text-white-75 mb-5">{heroSettings.subHeading}</h2>
                </div>
            </div>
        </Container>
      </header>

      {/* About Section */}
      <section className="page-section bg-light-custom py-5" id="about">
          <Container className="px-4 px-lg-5">
              <Row className="gx-4 gx-lg-5 justify-content-center align-items-center">
                  <Col lg={6} className="text-center mb-4 mb-lg-0">
                      <img className="img-fluid rounded-3 mb-4 shadow" src={`${API_URL}/uploads/headshot.jpg`} alt="Mike Williams Headshot" style={{ maxWidth: '300px', height: 'auto' }} />
                  </Col>
                  <Col lg={6}>
                      <h2 className="mt-0">About Mike</h2>
                      <hr className="divider my-4 bg-primary opacity-100" style={{ width: '100px', height: '3px' }} />
                      <p className="text-muted mb-4">Hello, I’m a Marketing Executive from Erie, Pennsylvania. I currently serve as Vice President of Marketing at a credit union, where I own the full marketing and advertising function and sit on the Executive team. I bring 15+ years of credit union experience and a working knowledge of nearly every side of the operation.</p>
                      <p className="text-muted mb-4">My sweet spot is the intersection of marketing and technology. I plan and buy media across TV, streaming, and digital, run the analytics behind it, and build the tools and automation that make a small team punch above its weight. I also lead a team of marketing and business development professionals, and I believe deeply in showing up for the community.</p>
                      <p className="text-muted">Skipped kindergarten. Loves the Pittsburgh Penguins. Tech Geek.</p>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* Skills Section */}
      <section className="page-section bg-white" id="skills">
          <Container className="px-4 px-lg-5">
              <Row className="justify-content-center">
                  <Col lg={8} className="text-center mb-5">
                      <h2 className="mt-0">Skills & Expertise</h2>
                      <hr className="divider my-4 bg-primary opacity-100 mx-auto" style={{ width: '100px', height: '3px' }} />
                      <p className="text-muted mb-0">What I bring to the table.</p>
                  </Col>
              </Row>
              <Row className="gx-4 gx-lg-5">
                  <Col md={6} xl={3} className="mb-4">
                      <Card className="h-100 border-0 shadow-sm">
                          <Card.Body className="p-4">
                              <div className="fs-1 text-primary mb-3 text-center"><FaBullhorn /></div>
                              <Card.Title className="fw-bold">Marketing Leadership</Card.Title>
                              <ul className="text-muted small mt-3 mb-0 ps-3">
                                  <li className="mb-2">Brand strategy and full campaign ownership</li>
                                  <li className="mb-2">Annual marketing planning and budget management</li>
                                  <li className="mb-2">Executive-level reporting and board presentations</li>
                                  <li>Leading marketing, business development, and intern teams</li>
                              </ul>
                          </Card.Body>
                      </Card>
                  </Col>
                  <Col md={6} xl={3} className="mb-4">
                      <Card className="h-100 border-0 shadow-sm">
                          <Card.Body className="p-4">
                              <div className="fs-1 text-primary mb-3 text-center"><FaChartLine /></div>
                              <Card.Title className="fw-bold">Digital & Analytics</Card.Title>
                              <ul className="text-muted small mt-3 mb-0 ps-3">
                                  <li className="mb-2">Google Ads, Analytics (GA4), and Tag Manager</li>
                                  <li className="mb-2">SEO, landing pages, and conversion tracking</li>
                                  <li className="mb-2">Email marketing and automation</li>
                                  <li>Social media strategy and content programs</li>
                              </ul>
                          </Card.Body>
                      </Card>
                  </Col>
                  <Col md={6} xl={3} className="mb-4">
                      <Card className="h-100 border-0 shadow-sm">
                          <Card.Body className="p-4">
                              <div className="fs-1 text-primary mb-3 text-center"><FaTv /></div>
                              <Card.Title className="fw-bold">Media & Advertising</Card.Title>
                              <ul className="text-muted small mt-3 mb-0 ps-3">
                                  <li className="mb-2">TV and streaming media planning and buying</li>
                                  <li className="mb-2">Sponsorships and community partnerships</li>
                                  <li className="mb-2">Creative direction and design</li>
                                  <li>Vendor and agency management</li>
                              </ul>
                          </Card.Body>
                      </Card>
                  </Col>
                  <Col md={6} xl={3} className="mb-4">
                      <Card className="h-100 border-0 shadow-sm">
                          <Card.Body className="p-4">
                              <div className="fs-1 text-primary mb-3 text-center"><FaRobot /></div>
                              <Card.Title className="fw-bold">Technology & AI</Card.Title>
                              <ul className="text-muted small mt-3 mb-0 ps-3">
                                  <li className="mb-2">AI-powered workflows and marketing automation</li>
                                  <li className="mb-2">Web development (React, Node.js, MongoDB)</li>
                                  <li className="mb-2">CRM and martech implementation</li>
                                  <li>Reporting dashboards and data integration</li>
                              </ul>
                          </Card.Body>
                      </Card>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* Work Experience Section */}
      <section className="page-section bg-light-custom" id="experience">
          <Container className="px-4 px-lg-5">
              <Row className="justify-content-center">
                  <Col lg={8} className="text-center mb-5">
                      <h2 className="mt-0">Work Experience</h2>
                      <hr className="divider my-4 bg-primary opacity-100 mx-auto" style={{ width: '100px', height: '3px' }} />
                      <p className="text-muted mb-0">A history of my professional roles and responsibilities.</p>
                  </Col>
              </Row>
              <Row className="justify-content-center">
                  <Col lg={8}>
                      <div className="timeline">
                          {work.map((job) => (
                              <div className="timeline-item" key={job._id}>
                                  <div className="timeline-dot"></div>
                                  <span className="timeline-date">
                                    {new Date(job.startDate).getFullYear()} - {job.isCurrent ? 'Present' : new Date(job.endDate).getFullYear()}
                                  </span>
                                  {job.logo && <img src={`${API_URL}${job.logo}`} alt={`${job.company} Logo`} className="timeline-logo" />}
                                  <h3 className="h4 mb-1">{job.title}</h3>
                                  <div className="timeline-company">{job.company}</div>
                                  <p className="text-muted mt-2">{job.description}</p>
                              </div>
                          ))}
                      </div>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* Blog section temporarily pulled (2026-07-29, job-search focus); see BlogPage/BlogPostPage to restore */}

      {/* Award / Highlights Section */}
      {highlights.map((item) => (
        <section className="page-section bg-white-custom" id="award" key={item._id}>
            <Container className="px-4 px-lg-5">
                <Row className="gx-4 gx-lg-5 justify-content-center align-items-center">
                    <Col lg={6} className="order-lg-2 text-center mb-4 mb-lg-0">
                         {item.image && <img className="img-fluid rounded-3 shadow-lg mb-4" src={`${API_URL}${item.image}`} alt={item.title} style={{ maxHeight: '300px', width: 'auto' }} />}
                    </Col>
                    <Col lg={6} className="order-lg-1">
                        <h2 className="mt-0">{item.title}</h2>
                        <hr className="divider my-4 bg-primary opacity-100" style={{ width: '100px', height: '3px' }} />
                        {item.subtitle && <h4 className="mb-3">{item.subtitle}</h4>}
                        <p className="text-muted mb-4">{item.description}</p>
                    </Col>
                </Row>
            </Container>
        </section>
      ))}

      {/* Community / Volunteer Service Section */}
      <section className="page-section" id="community">
          <Container className="px-4 px-lg-5">
              <Row className="justify-content-center">
                  <Col lg={8} className="text-center mb-5">
                      <h2 className="mt-0">Community & Volunteer Service</h2>
                      <hr className="divider my-4 bg-primary opacity-100 mx-auto" style={{ width: '100px', height: '3px' }} />
                      <p className="text-muted mb-0">Erie is home. Giving time back to the community is a non-negotiable part of how I work and live.</p>
                  </Col>
              </Row>
              <Row className="gx-4 gx-lg-5 justify-content-center">
                  {nonProfits.map((np) => (
                      <Col md={6} lg={4} className="mb-4" key={np._id}>
                          <Card className="h-100 border-0 shadow-sm text-center">
                              <Card.Body className="p-4 d-flex flex-column">
                                  {np.logo && (
                                      <div className="d-flex align-items-center justify-content-center mb-3" style={{ height: '90px' }}>
                                          <img src={`${API_URL}${np.logo}`} alt={np.title} style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }} />
                                      </div>
                                  )}
                                  <Card.Title className="fw-bold h5">{np.title}</Card.Title>
                                  <Card.Text className="text-muted small mb-0">{np.description}</Card.Text>
                              </Card.Body>
                          </Card>
                      </Col>
                  ))}
              </Row>
          </Container>
      </section>

      {/* Off the Clock — intro band for Spotify + GHIN */}
      <section className="page-section bg-white-custom pb-0" id="offtheclock">
          <Container className="px-4 px-lg-5">
              <Row className="justify-content-center">
                  <Col lg={8} className="text-center">
                      <h2 className="mt-0">Off the Clock</h2>
                      <hr className="divider my-4 bg-primary opacity-100 mx-auto" style={{ width: '100px', height: '3px' }} />
                      <p className="text-muted mb-0">Not everything needs a KPI. Live from my Spotify and GHIN accounts: what's on rotation and where the handicap stands.</p>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* Spotify Section */}
      <SpotifyStats />

      {/* GHIN Section */}
      <GhinStats />

      {/* Contact Section */}
      <section className="page-section bg-dark text-white" id="contact">
          <Container className="px-4 px-lg-5">
              <Row className="gx-4 gx-lg-5 justify-content-center">
                  <Col lg={8} xl={6} className="text-center">
                      <h2 className="mt-0">Get in Touch!</h2>
                      <hr className="divider my-4 bg-light opacity-100 mx-auto" style={{ width: '100px', height: '3px' }} />
                      <p className="text-white-75 mb-5">Have a question or just want to say hi? Send me a message and I will get back to you as soon as possible!</p>
                  </Col>
              </Row>
              <Row className="gx-4 gx-lg-5 justify-content-center mb-5">
                  <Col lg={6}>
                      {contactStatus === 'success' && <div className="alert alert-success">Message sent successfully!</div>}
                      {contactStatus === 'error' && <div className="alert alert-danger">Failed to send message. Please try again later.</div>}
                      
                      <form id="contactForm" onSubmit={handleContactSubmit}>
                          <div className="form-floating mb-3">
                              <input 
                                className="form-control" 
                                id="name" 
                                type="text" 
                                placeholder="Enter your name..." 
                                required 
                                value={contactForm.name}
                                onChange={handleContactChange}
                              />
                              <label htmlFor="name" className="text-dark">Full name</label>
                          </div>
                          <div className="form-floating mb-3">
                              <input 
                                className="form-control" 
                                id="email" 
                                type="email" 
                                placeholder="name@example.com" 
                                required 
                                value={contactForm.email}
                                onChange={handleContactChange}
                              />
                              <label htmlFor="email" className="text-dark">Email address</label>
                          </div>
                          <div className="form-floating mb-3">
                              <input 
                                className="form-control" 
                                id="phone" 
                                type="tel" 
                                placeholder="(123) 456-7890" 
                                value={contactForm.phone}
                                onChange={handleContactChange}
                              />
                              <label htmlFor="phone" className="text-dark">Phone number</label>
                          </div>
                          <div className="form-floating mb-3">
                              <textarea 
                                className="form-control" 
                                id="message" 
                                placeholder="Enter your message here..." 
                                style={{ height: '10rem' }} 
                                required
                                value={contactForm.message}
                                onChange={handleContactChange}
                              ></textarea>
                              <label htmlFor="message" className="text-dark">Message</label>
                          </div>

                          {/* Honeypot Field (Hidden) */}
                          <div style={{ display: 'none', opacity: 0, position: 'absolute', left: '-9999px' }}>
                              <input 
                                type="text" 
                                id="honeypot" 
                                value={contactForm.honeypot} 
                                onChange={handleContactChange} 
                                tabIndex="-1"
                                autoComplete="off"
                              />
                          </div>

                          {/* Math Challenge */}
                          <div className="form-floating mb-3">
                              <input 
                                className="form-control" 
                                id="mathAnswer" 
                                type="text" 
                                placeholder="5 + 2 = ?" 
                                required
                                value={contactForm.mathAnswer}
                                onChange={handleContactChange}
                              />
                              <label htmlFor="mathAnswer" className="text-dark">Security Question: What is 5 + 2?</label>
                          </div>

                          <div className="d-grid">
                            <button 
                              className={`btn btn-primary btn-xl ${contactStatus === 'sending' ? 'disabled' : ''}`} 
                              id="submitButton" 
                              type="submit"
                            >
                              {contactStatus === 'sending' ? 'Sending...' : 'Submit'}
                            </button>
                          </div>
                      </form>
                  </Col>
              </Row>
              <Row className="gx-4 gx-lg-5 justify-content-center">
                  <Col lg={4} className="text-center mb-5 mb-lg-0">
                      <div className="fs-2 mb-3 text-primary"><FaPhone /></div>
                      <div>814.796.7180</div>
                  </Col>
                  <Col lg={4} className="text-center">
                      <div className="fs-2 mb-3 text-primary"><FaEnvelope /></div>
                      <div><a href="mailto:mike@mikewilliams.us" className="text-white">mike@mikewilliams.us</a></div>
                  </Col>
              </Row>
          </Container>
      </section>

      {/* Footer */}
      <footer className="bg-light py-5">
          <Container className="px-4 px-lg-5">
              <div className="text-center mb-4">
                  <a className="text-secondary me-4 text-decoration-none" href="https://www.linkedin.com/in/mikew07/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                      <FaLinkedin size={32} />
                  </a>
                  <a className="text-secondary text-decoration-none" href="https://www.instagram.com/mikew07/" target="_blank" rel="noreferrer" aria-label="Instagram">
                      <FaInstagram size={32} />
                  </a>
              </div>
              <div className="small text-center text-muted">Copyright &copy; 2025 - Mike Williams</div>
          </Container>
      </footer>
    </>
  );
};

export default HomePage;
